import React, {
  useEffect,
  useCallback,
  useState,
  useRef,
  useContext,
} from 'react'
import NavLinks from '../NavLinks/NavLinks'
import { useNavigate, useSearchParams } from 'react-router-dom'
import AuthenticatedRoutes from '../../Utils/AuthenticatedRoutes'
import {
  fetchSummary,
  fetchEventTags,
  fetchComponentData,
} from '../../Api/transcriptAndSummary'
import { addPrompt, getVideoDownloadUrl } from '../../Api/meetings'
import Dropdown from '../Dropdown/Dropdown'
import AddTagPopup from './AddTagPopup'
import ActionItems from './ActionItems'
import Loader from '../Loader/Loader'
import './transcriptSummary.css'
import Feedback from '../Feedback/Feedback'
import logAppEvent from '../../Api/appEventsLogging'
import { useSelector } from 'react-redux'
import { FiExternalLink } from 'react-icons/fi'
import { Box, Button, Flex, Image, Input, Text } from '@chakra-ui/react'

import { Platform } from '../../Context/PlatformContext'
import { ask } from '../../Api/search'
import Tag from './Tag'
import { firstMeetingAnswer, firstMeetingData } from '../../Utils/data'
import { GetStartedContext } from '../../Context/GetStartedContext'

const monthsShort = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

const Summary = () => {
  const selectSpeakerButtonRef = useRef(null)
  const [isLoading, setIsLoading] = useState(true)
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const [data, setData] = useState(null)
  const [users, setUsers] = useState([])
  const [summaries, setSummaries] = useState([])
  const [botVisited, setBotVisited] = useState(false)
  const [eventTags, setEventTags] = useState([])
  const [selectedSpeaker, setSelectedSpeaker] = useState([])

  const user = useSelector((state) => state?.user?.data)
  const [search, setSearch] = useState('')
  const [showSearch, setShowSearch] = useState(false)
  const [isSearchLoading, setIsSearchLoading] = useState(false)
  const [answer, setAnswer] = useState('')
  const [dummyFirstMeetingData, setDummyFirstMeetingData] = useState()
  const [dummySummary, setDummySummary] = useState([])
  const searchInputRef = useRef()

  const platform = useContext(Platform)
  const { moveToNextStep, steps } = useContext(GetStartedContext)
  
  const [newPrompt, setNewPrompt] = useState('')
  const [newPromptLoading, setNewPromptLoading] = useState(false)
  
  const [showCardStatic, setShowCardStatic] = useState(true)
  const [templatisedNotes, setTemplatisedNotes] = useState([])
  useEffect(() => {
    let firstName = user?.name?.split(' ')[0]
    setDummyFirstMeetingData(
      firstMeetingData(firstName?.charAt(0).toUpperCase() + firstName?.slice(1))
    )
  }, [user])

  const {currentStep} = useContext(GetStartedContext)

  const fetchSummaryData = useCallback(async () => {
    const eventId = searchParams.get('eventId')
    const tab = searchParams.get('tab')
    setDummySummary(dummyFirstMeetingData?.event.summary)
    const apiResponse = await fetchSummary(eventId, tab)
    if (apiResponse && apiResponse?.event) {
      const usersData = []
      const date = apiResponse.event?.start_time
        ? new Date(Number(apiResponse.event.start_time))
        : new Date()
      apiResponse.event.start = `${
        monthsShort[date.getMonth()]
      } ${date.getDate()}, ${date.getFullYear()}`
      apiResponse.summaries.forEach((summary) => {
        if (usersData.indexOf(summary.user) === -1) {
          usersData.push(summary.user)
        }
      })
      setUsers([
        ...usersData.sort().map((user) => ({
          value: user,
          label: user,
        })),
      ])
      setData({
        ...apiResponse,
      })
      if (apiResponse.summaries?.length > 0) {
        setSummaries([...apiResponse.summaries])
      } else {
        setSummaries([])
      }

      if (apiResponse.botVisited) {
        setBotVisited(apiResponse.botVisited)
      }
    } else {
      setData(null)
      setUsers([])
      setSummaries([])
    }
    if (isLoading) {
      setIsLoading(false)
    }
  }, [searchParams, isLoading, setIsLoading])

  const fetchTemplatisedNotesComponentData = useCallback(async () => {
    const eventId = searchParams.get('eventId')
    // console.log(eventId)
    const apiResponse = await fetchComponentData(
      'templatisedNotes',
      [`event_${eventId}`],
      undefined,
      undefined
    )
    // console.log('apiResponse', apiResponse)
    if (apiResponse) {
      setTemplatisedNotes(apiResponse)
    }
    if (isLoading) {
      setIsLoading(false)
    }
  }, [searchParams, isLoading, setIsLoading])

  const fetchEventTagsData = useCallback(async () => {
    if (searchParams.get('eventId')) {
      const apiResponse = await fetchEventTags(searchParams.get('eventId'))
      if (apiResponse?.length > 0) {
        setEventTags([...apiResponse])
      } else {
        setEventTags([])
      }
    }
  }, [searchParams, setEventTags])

  useEffect(() => {
    if (searchParams.get('eventId')) {
      fetchSummaryData()
      fetchEventTagsData()
      fetchTemplatisedNotesComponentData()
    } else {
      navigate('/transcripts-summaries/summary?eventId=latest-event&tab=HIGH')
    }
  }, [searchParams, fetchSummaryData, fetchEventTagsData, navigate])

  const selctedSpeakerHandler = useCallback(() => {
    if (selectedSpeaker?.length > 0) {
      if (data?.summaries?.length > 0) {
        setSummaries([
          // ...data?.summaries?.filter(
          // 	(item) => selectedSpeaker.indexOf(item.user) !== -1
          // ),
          ...(data?.summaries?.filter(
            (item) => selectedSpeaker.indexOf(item.user) !== -1
          ) ?? []),
        ])
      } else {
        setSummaries([])
      }
    } else {
      if (data?.summaries?.length > 0) {
        setSummaries([...data.summaries])
      } else {
        setSummaries([])
      }
    }
  }, [data, selectedSpeaker])

  const getVideoDownloadUrlHandler = useCallback(async () => {
    const apiResponse = await getVideoDownloadUrl(data.event.event_id)
    if (apiResponse) {
      const link = document.createElement('a')
      link.href = apiResponse
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  }, [data])

  useEffect(() => {
    selctedSpeakerHandler()
  }, [selectedSpeaker, selctedSpeakerHandler])

  const handleHighLevelSummaryClick = () => {
    logAppEvent('transumm_high_leveled_summary_click', {
      user_id: user?.id,
      event_id: data?.event?.event_id,
    })
    navigate(
      `/transcripts-summaries/summary?eventId=${searchParams.get(
        'eventId'
      )}&tab=HIGH&eventName=${searchParams.get(
        'eventName'
      )}`
    )
  }

  const handleDetailedSummaryClick = () => {
    logAppEvent('transumm_detailed_summary_click', {
      user_id: user?.id,
      event_id: data?.event?.event_id,
    })
    navigate(
      `/transcripts-summaries/summary?eventId=${searchParams.get(
        'eventId'
      )}&tab=LOW&eventName=${searchParams.get(
        'eventName'
      )}`
    )
  }

  const handleToggle = () => {
    const eventId = searchParams.get('eventId')

    logAppEvent('transumm_transcript_toggle_click', {
      user_id: user?.id,
      event_id: eventId,
    })
    navigate(
      `/transcripts-summaries/transcript?eventId=${searchParams.get('eventId')}&eventName=${searchParams.get('eventName')}`
    )
  }

  const handleAddPrompt = async (prompt) => {
    setNewPromptLoading(true)
    const eventId = searchParams.get('eventId')
    await addPrompt(eventId, prompt, 'event')
    setNewPrompt('')
    setNewPromptLoading(false)
  }

  
  const submitHandler = useCallback(
    async (event) => {
      event.preventDefault()
      setIsSearchLoading(true)
      // console.log(firstMeetingAnswer)

      try {
        if (
          searchParams.get('eventId') === `${user?.id}_google_first_meeting`
        ) {
          setAnswer(firstMeetingAnswer)
          setIsSearchLoading(false)
          // console.log(answer)
        } else if (search.trim()) {
          const apiResponse = await ask(search.trim())
          setIsSearchLoading(false)
          if (apiResponse?.answer[0]?.answer) {
            setAnswer(apiResponse?.answer[0]?.answer)
          } else {
            setAnswer('')
          }
        }
      } catch (e) {
        // console.log(e)
        setIsSearchLoading(false)
      }
    },
    [search]
  )
  const stepId = steps.findIndex((step) => step.status === 'ACTIVE')
  // useEffect(() => {
  //   document.addEventListener('keydown', (event) => {
  //     if (
  //       (platform === 'Mac' &&
  //         event.metaKey &&
  //         event.key.toUpperCase() === 'K') ||
  //       (platform === 'Windows' &&
  //         event.ctrlKey &&
  //         event.key.toUpperCase() === 'G')
  //     ) {
  //       event.preventDefault()
  //       setShowSearch(true)
  //       if (stepId === 3) {
  //         moveToNextStep(stepId)
  //       }
  //       setSearch('')
  //       setAnswer('')
  //     }
  //     if (event.key.toUpperCase() === 'ESCAPE') {
  //       setShowSearch(false)
  //       setSearch('')
  //       setAnswer('')
  //     }
  //   })
  //   return () => document.removeEventListener('keydown', () => {})
  // }, [showSearch, setShowSearch])

  // useEffect(() => {
  //   if (steps){
  //     const stepId = steps.findIndex((step) => step.status === 'ACTIVE')
  //     document.addEventListener('keydown', (event) => {
  //       if (
  //         (platform === 'Mac' &&
  //           event.metaKey &&
  //           event.key.toUpperCase() === 'K') ||
  //         (platform === 'Windows' &&
  //           event.ctrlKey &&
  //           event.key.toUpperCase() === 'G')
  //       ) {
  //         event.preventDefault()
  //         console.log(stepId)
  //         if (stepId === 3) {
  //           moveToNextStep(stepId)
  //         }
  //         setShowSearch(true)

  //         searchInputRef.current.focus()
  //         setSearch('')
  //         setAnswer('')
  //       }
  //       if (event.key.toUpperCase() === 'ESCAPE') {
  //         setShowSearch(false)
  //         setSearch('')
  //         setAnswer('')
  //       }
  //     })
  //     return () => document.removeEventListener('keydown', () => {})
  //   }
    
  // }, [showSearch, setShowSearch, steps])

  const handleShowSearchBar = () => {
    setShowSearch(true)
    searchInputRef.current.focus()
    setSearch('')
    setAnswer('')
  }

  return (
    <div className='container container_light ask transcript-summary'>
      <NavLinks />
      <AuthenticatedRoutes auth={true} />
      <div
        className={showSearch ? 'search-contanr active' : 'search-contanr'}
        style={{ marginLeft: '1rem' }}
      >
        <div
          onClick={() => {
            setShowSearch(false)
            setSearch('')
            setAnswer('')
          }}
          style={{
            height: '100%',
            width: '100%',
            position: 'absolute',
          }}
        />
        <div style={{ zIndex: 1 }} className='search-wrapper'>
          <div className='question-box'>
            <form onSubmit={submitHandler}>
              <input
                value={
                  searchParams.get('eventId') ===
                  `${user?.id}_google_first_meeting`
                    ? 'Summarize the main objectives and goals in this meeting'
                    : search
                }
                onChange={(e) => setSearch(e.target.value)}
                autoFocus
                ref={searchInputRef}
                placeholder='Enter question to Ask Arrowhead about meetings...'
              />
            </form>
            <div className='actions'>
              <h4>
                Searching within <span>{data?.event.title}</span>
              </h4>
              {/* <button>See previous questions</button> */}
            </div>
          </div>
          {isSearchLoading && <Loader />}
          {!isSearchLoading && answer && (
            <div className='answer-box'>
              <p>{answer}</p>
              <button style={{ color: 'black' }}>
                Ask another question{' '}
                {platform === 'Mac' ? (
                  <>
                    <span>⌘</span>
                    <span>K</span>
                  </>
                ) : (
                  <>
                    <span>Ctrl</span>
                    <span>G</span>
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {searchParams.get('eventId') === `${user?.id}_google_first_meeting` ||
      searchParams.get('eventId') === `${user?.id}_google_tag_meeting` ? (
          <div className='transript-summary-container'>
            {data?.event && (
              <>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '1rem',
                  }}
                >
                  <h1>{data?.event?.title}</h1>
                  {searchParams
                    ?.get('eventId')
                    ?.split('_')?.[1]
                    ?.endsWith('00Z') && (
                    <FiExternalLink
                      style={{ cursor: 'pointer' }}
                      color='black'
                      onClick={() =>
                        navigate(
                          `/transcripts-summaries/dashboard?name=rec_${searchParams.get(
                            'eventId'
                          )}`
                        )
                      }
                      size={16}
                    />
                  )}
                </div>
                <div className='tags-section'>
                  <div className='download-section'>
                    <h3 style={{ textAlign: 'center' }}>
                      {data?.event?.start && data?.event?.start}
                    </h3>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      {/* <Tag
                        eventId={`${user?.id}_google_first_meeting`}
                        tags={eventTags}
                        fetch={fetchEventTagsData}
                      /> */}

                      <button>
                        <img src='/assets/icons/download.svg' alt='' />
                      </button>
                    </div>
                  </div>
                </div>
                <div
                  style={{
                    opacity: botVisited ? 1 : 0.4,
                    pointerEvents: botVisited ? 'auto' : 'none',
                  }}
                  className='switch-box'
                >
                  <h3 className='strong'>Summary</h3>
                  <label className='switch'>
                    <input type='checkbox' onChange={handleToggle} />
                    <span className='slider round'></span>
                  </label>
                  <h3>Transcript</h3>
                </div>
                {
                  <>
                    <div className='tabs-section'>
                      <div className='summary-tabs'>
                        <button
                          className={
                            searchParams.get('tab') === 'HIGH' ? 'active' : ''
                          }
                        >
                        Quick
                        </button>
                        <button>Detailed</button>
                        <br />
                      </div>

                      <div className='asktsbar' style={{ marginLeft: '1rem' }}>
                        <div className='tag'>Ask Arrowhead</div>
                        {platform === 'Mac' ? (
                          <>
                            <div className='key'>⌘</div>
                            <div className='key'>K</div>
                          </>
                        ) : (
                          <>
                            <div className='key'>Ctrl</div>
                            <div className='key'>G</div>
                          </>
                        )}
                      </div>
                    </div>
                  </>
                }
                <div className='transcript-summary-content'>
                  <div className='summary-content-wrapper'>
                    {dummySummary?.length > 0 && (
                      <div className='content summary'>
                        {dummySummary?.map((summary, index) => (
                          <p key={summary.id}>
                            {summary?.summary?.split('•').map((item, index) => (
                              <React.Fragment key={index}>
                                {index !== 0 && '• ' + item.trim()}
                                {index !== 0 && (
                                  <>
                                    <br />
                                    <br />
                                  </>
                                )}
                                {index === 0 && item.trim() ? item.trim() : ''}
                              </React.Fragment>
                            ))}
                            {index !== summaries.length - 1 && (
                              <>
                                <br />
                                <br />
                              </>
                            )}
                          </p>
                        ))}
                      </div>
                    )}
                    <Feedback
                      feedback_type={1}
                      reference_id={data?.event?.event_id}
                    />

                    {dummySummary?.length > 0 &&
                    dummyFirstMeetingData.event.templatisedNotes &&
                    dummyFirstMeetingData.event.templatisedNotes.length &&
                    dummyFirstMeetingData.event.templatisedNotes.map(
                      ({ question, answer }) => (
                        <>
                          <Text
                            mt={8}
                            mb={2}
                            fontWeight='600'
                            color='rgba(60, 60, 60, 0.6)'
                          >
                            {question}
                          </Text>

                          <div className='content summary'>
                            {/* TODO: change the comma to something else */}
                            {answer.split(',').map((answer, index) => (
                              <p key={`${question}-${index}`}>• {answer}</p>
                            ))}
                          </div>
                        </>
                      )
                    )}

                    <Box mt={4} />
                  </div>

                  <ActionItems
                    firstMeetActionItems={dummyFirstMeetingData.event.actionItems}
                  />
                {currentStep === 2 &&
                  showCardStatic &&
                  searchParams.get('eventId') ===
                    `${user?.id}_google_first_meeting` && (
                    <Box
                      position='fixed'
                      bottom='2'
                      right='4'
                      h='300px'
                      w='350px'
                      zIndex='100'
                    >
                      <Box
                        h='100%'
                        w='100%'
                        bg='linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), url(/assets/images/Gradient1.png) center/cover'
                        bgBlendMode=' multiply, normal'
                        boxShadow=' 0px 14px 6px rgba(0, 0, 0, 0.01), 0px 8px 5px rgba(0, 0, 0, 0.02), 0px 4px 4px rgba(0, 0, 0, 0.03), 0px 1px 2px rgba(0, 0, 0, 0.04), 0px 0px 0px rgba(0, 0, 0, 0.04)'
                        borderRadius='16px'
                        py={4}
                        px={3}
                      >
                        <Image
                          src='/assets/icons/lightining.svg'
                          mt='2rem'
                          px={3}
                        />
                        <Flex position='absolute' top='13px' right='13px'>
                          <Image opacity='1' boxSize={3} color='brandTextBlack' src='/assets/icons/cancel.svg' cursor='pointer' onClick={() => setShowCardStatic(false)} />
                        </Flex>
                        <Box my={2} px={4}>
                          <Text fontWeight='700' mb={2}>
                            Meet AI-crafted pointers
                          </Text>
                          <Text color='whiteAlpha.700'>
                            These insights are auto-generated by our clever AI,
                            saving you time and effort. Sit back and enjoy the
                            AI magic! 🧙‍♂️🤖
                          </Text>
                          {/* <Button
                            bgColor='transparent'
                            mt={4}
                            _hover={{ backgroundColor: 'transparent' }}
                            border='1px solid rgba(234, 234, 234, 0.4)'
                            borderRadius='8px'
                            onClick={() => setShowCardStatic(false)}
                          >
                            Ok
                          </Button> */}
                        </Box>
                      </Box>
                    </Box>
                  )}
                </div>
              </>
            )}
          </div>
        ) : isLoading ? (
          <Loader />
        ) : (
          <div className='transript-summary-container'>
            {data?.event && (
              <>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '1rem',
                  }}
                >
                  <h1>{data?.event?.title}</h1>
                  {searchParams
                    ?.get('eventId')
                    ?.split('_')?.[1]
                    ?.endsWith('00Z') && (
                    <FiExternalLink
                      style={{ cursor: 'pointer' }}
                      color='black'
                      onClick={() =>
                        navigate(
                          `/transcripts-summaries/dashboard?name=rec_${searchParams.get(
                            'eventId'
                          )}`
                        )
                      }
                      size={16}
                    />
                  )}
                </div>
                <div className='tags-section'>
                  <div className='download-section'>
                    <h3 style={{ textAlign: 'center' }}>
                      {data?.event?.start && data?.event?.start}
                    </h3>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      {/* <Tag
                        eventId={data?.event?.event_id}
                        tags={eventTags}
                        fetch={fetchEventTagsData}
                      /> */}
                      {data.event.video_url && (
                        <button onClick={getVideoDownloadUrlHandler}>
                          <img src='/assets/icons/download.svg' alt='' />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                <div
                  style={{
                    opacity: botVisited ? 1 : 0.4,
                    pointerEvents: botVisited ? 'auto' : 'none',
                  }}
                  className='switch-box'
                >
                  <h3 className='strong'>Summary</h3>
                  <label className='switch'>
                    <input type='checkbox' onChange={handleToggle} />
                    <span className='slider round'></span>
                  </label>
                  <h3>Transcript</h3>
                </div>
                {botVisited && (
                  <>
                    <div className='tabs-section'>
                      <div className='summary-tabs'>
                        <button
                          className={
                            searchParams.get('tab') === 'HIGH' ? 'active' : ''
                          }
                          onClick={() => handleHighLevelSummaryClick()}
                        >
                        Quick
                        </button>
                        <button
                          className={
                            searchParams.get('tab') === 'LOW' ? 'active' : ''
                          }
                          onClick={() => handleDetailedSummaryClick()}
                        >
                        Detailed
                        </button>
                        <br />
                      </div>
                      <div
                        className='asktsbar'
                        style={{ marginLeft: '1rem', cursor: 'pointer' }}
                        onClick={handleShowSearchBar}
                      >
                        <div className='tag'>Ask Arrowhead</div>
                        {platform === 'Mac' ? (
                          <>
                            <div className='key'>⌘</div>
                            <div className='key'>K</div>
                          </>
                        ) : (
                          <>
                            <div className='key'>Ctrl</div>
                            <div className='key'>G</div>
                          </>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
            <div className='transcript-summary-content'>
              <div className='summary-content-wrapper'>
                {summaries?.length > 0 ? (
                  <div className='content summary'>
                    {summaries?.map((summary, index) => (
                      <p key={summary.id}>
                        {summary?.summary?.split('•').map((item, index) => (
                          <React.Fragment key={index}>
                            {index !== 0 && '• ' + item.trim()}
                            {index !== 0 && (
                              <>
                                <br />
                                <br />
                              </>
                            )}
                            {index === 0 && item.trim() ? item.trim() : ''}
                          </React.Fragment>
                        ))}
                        {index !== summaries.length - 1 && (
                          <>
                            <br />
                            <br />
                          </>
                        )}
                      </p>
                    ))}
                  </div>
                ) : botVisited ? (
                  <div className='data-notfound' style={{ maxWidth: '393px' }}>
                    <strong>No summary generated</strong>
                    <p>
                    Your call was too short! You’re doing a great job of valuing
                    your time 🙌
                    </p>
                  </div>
                ) : (
                  <div className='data-notfound'>
                    <strong>
                    Looks like our Arrowhead Notetaker didn’t enter this
                    meeting!
                    </strong>
                    <p>
                    Allow ‘Arrowhead Notetaker’ into every meeting so we can
                    auto-build your knowledge repository for you!
                    </p>
                    <p>(We promise not to interrupt your conversation 😉)</p>
                  </div>
                )}
                <Feedback
                  feedback_type={2}
                  reference_id={data?.event?.event_id}
                />

                {summaries?.length > 0 &&
              templatisedNotes &&
              templatisedNotes.length ? (
                    templatisedNotes.map((note, index) => {
                      return Object.keys(note).map((question, questionIndex) => (
                        <Box key={`answer-${index}-${questionIndex}`}>
                          <Text
                            mt={8}
                            mb={2}
                            fontWeight='600'
                            color='rgba(60, 60, 60, 0.6)'
                        maxW='550px'
                          >
                            {question}
                          </Text>

                          <div className='content summary'>
                            {note[question].map(({ answer }, answerIndex) => (
                              <p
                                key={`answer-${index}-${questionIndex}-${answerIndex}`}
                              >
                            • {answer}
                              </p>
                            ))}
                          </div>
                        </Box>
                      ))
                    })
                  ) : (
                    <></>
                  )}

                <Box mt={4} />
              </div>
              {botVisited && <ActionItems eventId={data?.event?.event_id} />}
            </div>
          </div>
        )}
      {/* {currentStep === -1 && (
        <>
          <Box mb={12} />
          <Flex position='fixed' zIndex={20} left={0} bottom={4} w='full' justifyContent='center'>
            <Input
              bg='white'
              border='0.5px solid rgba(28, 28, 28, 0.1)'
              boxShadow='0px 16px 10px rgba(2, 13, 49, 0.02), 0px 7px 7px rgba(2, 13, 49, 0.03), 0px 2px 4px rgba(2, 13, 49, 0.03), 0px 0px 0px rgba(2, 13, 49, 0.03)'
              _focusVisible={{
                border: '0.5px solid rgba(28, 28, 28, 0.1)',
                boxShadow: '0px 16px 10px rgba(2, 13, 49, 0.02), 0px 7px 7px rgba(2, 13, 49, 0.03), 0px 2px 4px rgba(2, 13, 49, 0.03), 0px 0px 0px rgba(2, 13, 49, 0.03)'
              }}
              flex={1}
              maxW='707px'
              color='black'
              transition='none'
              value={newPrompt} 
              placeholder='Try asking “what are the blockers”'
              onChange={e => !setNewPromptLoading || setNewPrompt(e.target.value)} 
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleAddPrompt(newPrompt)
                }
              }}
            />
          </Flex>
        </>
      )} */}
    </div>
  )
}

export default Summary
